import { Injectable } from '@angular/core';
import { RequestService } from '@df/api/request/request.service';
import { RecentlyViewedService } from '@df/catalog-ui/recently-viewed/recently-viewed.service';
import { Model } from '@df/catalog/model';
import { App } from '@df/core/app';
import { CacheService } from '@df/core/cache/cache';
import compact from 'lodash-es/compact';
import { ModelCarouselEngine } from './model-carousel-engine';
import { EModelCarouselVariant, IModelCarouselLoadRequest } from './model-carousel.interface';

@Injectable({
    providedIn: 'root'
})
export class ModelCarouselService {
    static engines: { [id: string]: ModelCarouselEngine } = {};

    /**
     * Class constructor
     */
    constructor(
        protected cache: CacheService,
        protected app: App,
        protected requestService: RequestService,
        private recentlyViewedService: RecentlyViewedService
    ) {}

    /**
     * Unique cache ID getter
     */
    getUid(type: string, data: IModelCarouselLoadRequest, modelRelative?: boolean) {
        return [
            type,
            data.variant,
            data.category_id,
            compact(data.ids)?.join('-'),
            data.limit,
            data.verbosity,
            modelRelative
                ? [
                      this.app.meta.model?.type,
                      this.app.meta.model?.id,
                      data.isRecentlyViewed
                          ? this.recentlyViewedService
                                .getItems()
                                .map(item => item.id)
                                .join('-')
                          : ''
                  ].join('-')
                : ''
        ].join('-');
    }

    /**
     * Load models method
     */
    async loadModels(type: string, data: IModelCarouselLoadRequest) {
        const variant = data.variant;
        const engine = variant && ModelCarouselService.engines[variant];
        const modelRelative = engine?.isModelRelative;
        if (engine && modelRelative) {
            await this.app.meta.model?.load(2);
        }
        const cName = this.getUid(type, data, modelRelative);

        const cached = this.cache.get(cName);
        if (cached) {
            return cached;
        }

        let result: Model[] = [];

        if (engine) {
            result = await engine.get(type, data);
        } else {
            const resource = this.app.resources[type];
            const ids = compact(data.ids);
            const categoryId = data.category_id;

            switch (variant) {
                case EModelCarouselVariant.ModelsIds:
                    if (ids?.length) {
                        await resource.loadAllIds(ids, data.verbosity || 2, { safe: true });
                        result = resource.getAllExisting(ids);
                    }

                    break;
                case EModelCarouselVariant.CategoryProducts:
                    if (categoryId) {
                        result = await this.requestService.loadCategoryProducts({
                            verbosity: data.verbosity || 2,
                            limit: data.limit || 36,
                            category: categoryId
                        });
                    }
            }
        }
        if(type === 'product'){
            await this.app.resources.stock.loadAllIds(result.map(i => i.id as number), 1, {safe: true})
        }
        return this.cache.set(cName, result);
    }
}
